import React, { useEffect, useRef, useState } from 'react';
import {
    Badge, Box, Button, Flex, HStack, Icon, Input, InputGroup, InputLeftElement, Text, VStack,
} from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa';
import { buscarVendasLalamove } from '../../services/api';

/**
 * Procura uma venda para vincular a uma corrida Lalamove.
 *
 * Nasceu dentro do modal de importar corrida da pagina /lalamove e foi tirada
 * de la quando a corrida avulsa passou a precisar da mesma busca: dois lugares
 * pedindo "ache a venda" nao podem ter duas implementacoes, senao uma melhora
 * e a outra nao.
 *
 * Fala com o mesmo endpoint de sempre (/lalamove/buscar-vendas).
 */

const brl = (valor) => `R$ ${Number(valor || 0).toFixed(2).replace('.', ',')}`;

const Resumo = ({ venda }) => (
    <Text fontSize="xs" color="gray.600" noOfLines={1}>
        {venda.cliente || 'sem cliente'} · {venda.modo_envio || 'sem envio'}
        {venda.frete_cobrado !== null && venda.frete_cobrado !== undefined ? ` · frete ${brl(venda.frete_cobrado)}` : ''}
    </Text>
);

const BuscaVenda = ({
    ativa = true,
    escolhida = null,
    onEscolher,
    onLimpar,
    placeholder = 'venda, pedido, cliente ou código',
    autoFoco = false,
}) => {
    const [termo, setTermo] = useState('');
    const [encontradas, setEncontradas] = useState(null);
    const [buscando, setBuscando] = useState(false);
    const pedidoRef = useRef(0);

    useEffect(() => {
        if (!ativa) return undefined;

        const q = termo.trim();
        if (escolhida || q.length < 2) {
            setEncontradas(null);
            return undefined;
        }

        const id = setTimeout(async () => {
            setBuscando(true);
            // digitar rapido dispara buscas em sequencia e elas nao voltam em
            // ordem: sem o contador, a resposta velha sobrescreve a nova
            const meu = ++pedidoRef.current;
            try {
                const res = await buscarVendasLalamove(q);
                if (meu !== pedidoRef.current) return;
                setEncontradas(res.vendas || []);
            } catch (e) {
                if (meu === pedidoRef.current) setEncontradas([]);
            } finally {
                if (meu === pedidoRef.current) setBuscando(false);
            }
        }, 400);

        return () => clearTimeout(id);
    }, [termo, escolhida, ativa]);

    const limpar = () => {
        setTermo('');
        setEncontradas(null);
        if (onLimpar) onLimpar();
    };

    const escolher = (v) => {
        setTermo('');
        setEncontradas(null);
        if (onEscolher) onEscolher(v);
    };

    if (escolhida) {
        return (
            <Flex borderWidth="1px" borderColor="purple.200" bg="purple.50" borderRadius="lg" p={2} gap={2} align="center" justify="space-between">
                <Box minW={0}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.800">
                        #{escolhida.id}{escolhida.id_origem ? ` · ${escolhida.id_origem}` : ''}
                    </Text>
                    <Resumo venda={escolhida} />
                </Box>
                <Button size="xs" variant="ghost" onClick={limpar} flexShrink={0}>trocar</Button>
            </Flex>
        );
    }

    return (
        <>
            <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="gray.400" boxSize={3} />
                </InputLeftElement>
                <Input
                    borderRadius="lg"
                    placeholder={placeholder}
                    aria-label="Buscar venda"
                    value={termo}
                    autoFocus={autoFoco}
                    onChange={(e) => setTermo(e.target.value)}
                />
            </InputGroup>

            {buscando && <Text fontSize="xs" color="gray.400" mt={2}>procurando...</Text>}

            {!buscando && encontradas?.length === 0 && (
                <Text fontSize="xs" color="gray.400" mt={2}>Nenhuma venda encontrada.</Text>
            )}

            {!buscando && encontradas?.length > 0 && (
                <VStack
                    align="stretch" spacing={0} mt={2} borderWidth="1px" borderColor="gray.100"
                    borderRadius="lg" overflow="hidden" maxH="230px" overflowY="auto"
                >
                    {encontradas.map((v) => (
                        <Box
                            key={v.id}
                            as="button"
                            type="button"
                            textAlign="left"
                            px={3}
                            py={2}
                            borderBottomWidth="1px"
                            borderColor="gray.50"
                            _hover={{ bg: 'purple.50' }}
                            onClick={() => escolher(v)}
                        >
                            <Flex justify="space-between" gap={2} align="center">
                                <Text fontSize="sm" fontWeight="bold" color="gray.800">
                                    #{v.id}{v.id_origem ? ` · ${v.id_origem}` : ''}
                                </Text>
                                <HStack spacing={1} flexShrink={0}>
                                    {v.tem_corrida && <Badge colorScheme="orange" fontSize="9px">já tem corrida</Badge>}
                                    <Badge colorScheme={v.status === 'cancelada' ? 'red' : 'gray'} fontSize="9px">{v.status}</Badge>
                                </HStack>
                            </Flex>
                            <Resumo venda={v} />
                        </Box>
                    ))}
                </VStack>
            )}
        </>
    );
};

export default BuscaVenda;
